import { type CanvasRequest, canvasFootprint } from '@/play/canvasRequest'
import { EDGE_INSET } from '@/constants/insets'
import { pageBandPlacement } from '@/play/pageBand'
import { createPageDocument } from '@/play/pageDocument'
import { hash2, valueNoise } from '@/play/noise'
import { definePalette } from '@/play/palette'
import { worldCells } from '@/play/worldKit'
import type { Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'

/**
 * Moon slime 2: 009's grey cratered moon under a black sky (MoonScene adds the stars and Earth),
 * with the slime of the paint study: neon goo in the `shiny` material, every colour one surface.
 */
export const MOON_SLIME_2_ID = 'moon-slime-2'
/** 2 (2026-10-16): the moon takes the screen's shape (a tall moon on an upright phone). */
export const MOON_SLIME_2_VERSION = 2
/** The moon as first made, 64 x 64: the fallback size, and the frame its craters are laid out in. */
export const MOON_BOUNDS: Position = { x: 64, y: 32, z: 64 }

/** The paint study's neon goo (scripts/paintings/slime.ts, `slime-moon-v2`): 009's colours. */
export const SLIME_COLORS = [0x6dff3a, 0xff3fb4, 0x2ef2ff, 0xffe53a] as const

const ROCK = [0x8c8e95, 0x9a9ca3, 0xa9abb1, 0xb8babf] as const
const ROCK_VOXELS: VoxelData[] = ROCK.map((color) => ({ type: 'solid', color }))
/** Slime: blob goo, shiny (saturated enamel, the study's goo). */
export const slimeVoxel = (color: number): VoxelData => ({ type: 'blob', color, material: 'shiny' })

/**
 * Googly eye balls (since 2026-10-15; owner: "they are often completely buried in the slime and i
 * can't see them - maybe more like the slime family ones?"): 031's eyes, a glossy white ball half
 * out of the surface with a pupil that follows the finger (`hints.eyeBalls`), instead of flat eye
 * squares. Four whites too close to tell apart, so touching eyes stay two balls (`pickEyeWhite`).
 */
export const MOON_EYE_WHITES: readonly VoxelData[] = [0xfbfbf6, 0xf8f9fb, 0xfbf8f9, 0xf9fbf8].map((color) => ({ type: 'blob', color, material: 'glossy' }))
export const MOON_PUPIL_COLOR = 0x15131c
export const isMoonEyeWhite = (v: VoxelData | undefined) =>
  v !== undefined && MOON_EYE_WHITES.some((w) => w.type === v.type && w.color === v.color && w.material === v.material)

/**
 * The rock, the slimes (`MOON_SLIME_2_PALETTE.at('slimes', i)` for the draw tool), then the same
 * slimes in 009's glossy material (the tuning panel's comparison), then the eye whites.
 */
export const MOON_SLIME_2_PALETTE = definePalette({
  rock: ROCK_VOXELS,
  slimes: SLIME_COLORS.map(slimeVoxel),
  glossySlimes: SLIME_COLORS.map((color): VoxelData => ({ type: 'blob', color, material: 'glossy' })),
  eyes: MOON_EYE_WHITES,
})

/*
 * The surface is endless (noise and craters, in the 64 x 64 moon's coordinates): a moon of the
 * screen's shape is the piece of it centred on the first moon, so its middle is the moon as it was,
 * cropped on the short side and extended on the long one. The craters past the first 64 x 64 only
 * show on an extended moon.
 */
const CRATERS = [
  { x: 22, z: 22, r: 11, depth: 5, rim: 3 },
  { x: 46, z: 40, r: 8, depth: 4, rim: 2.5 },
  { x: 48, z: 14, r: 5, depth: 3, rim: 2 },
  { x: 16, z: 48, r: 6, depth: 3, rim: 2 },
  { x: 34, z: 54, r: 3.5, depth: 2, rim: 1.2 },
  // Beyond the first moon: up and down an upright phone's tall moon, and to the sides of a wide one.
  { x: 30, z: -14, r: 7, depth: 3.5, rim: 2.2 },
  { x: 52, z: -34, r: 4.5, depth: 2.5, rim: 1.6 },
  { x: 40, z: 80, r: 9, depth: 4, rim: 2.5 },
  { x: 12, z: 98, r: 5, depth: 2.5, rim: 1.6 },
  { x: -14, z: 30, r: 7, depth: 3.5, rim: 2.2 },
  { x: 80, z: 24, r: 6, depth: 3, rim: 2 },
  { x: 92, z: 52, r: 4, depth: 2, rim: 1.4 },
] as const

function moonHeight(x: number, z: number): number {
  let h = 7 + 2.2 * valueNoise(x, z, 16, 3) + 0.8 * valueNoise(x, z, 5, 9)
  for (const c of CRATERS) {
    const d = Math.hypot(x - c.x, z - c.z) / c.r
    // Raised rim around the edge, a round bowl inside.
    h += c.rim * Math.exp(-(((d - 1) / 0.28) ** 2))
    if (d < 1) h -= c.depth * (1 - d * d)
  }
  return Math.max(1, Math.round(h))
}

/**
 * A new moon takes the screen's shape with about the first moon's cell count, beside the slimes'
 * band (owner, 2026-10-16: "would be nice if the moon surface would fit the screen proportion").
 * A remade world keeps its own bounds (`canvas.keep`).
 */
export function createMoonSlime2World(canvas?: CanvasRequest) {
  // The slimes, Eyes and the spade ride the band (along the top, or the bottom on an upright phone): kept clear.
  const band = canvas && !canvas.keep ? pageBandPlacement(canvas.width, SLIME_COLORS.length + 2) : null
  const inset = band ? { [band.region.edge]: band.height + EDGE_INSET } : {}
  const B: Position = canvas?.keep ? { ...canvas.keep } : { ...MOON_BOUNDS, ...canvasFootprint(canvas, { fallback: MOON_BOUNDS, inset, min: 40 }) }
  // This moon's piece of the endless surface, centred on the first moon.
  const ox = (B.x - MOON_BOUNDS.x) / 2
  const oz = (B.z - MOON_BOUNDS.z) / 2
  // moonHeight cells per column, speckled grey dust, a little darker in the low places (as 009).
  const w = worldCells(B).columns((x, z) => moonHeight(x - ox, z - oz) - 1, (x, y, z, top) => {
    const shade = Math.min(ROCK.length - 1, Math.max(0, Math.floor(hash2(x * 7 + y, z, 11) * 2.2 + (top - 4) / 3)))
    return ROCK_VOXELS[shade]
  })
  return createPageDocument(w.map, {
    page: { id: MOON_SLIME_2_ID, version: MOON_SLIME_2_VERSION },
    bounds: B,
    palette: MOON_SLIME_2_PALETTE.list,
    backgroundColor: 0x070915,
    // Looking up the moon from its near edge, its rectangle framed to the screen beside the band.
    camera: { phi: 0.9, theta: 0, zoom: 0.95, fit: 'footprint', inset },
    // 009's low sun from one side; the page's raking rig (MoonSlime2Shell) takes over while open.
    light: { x: -B.x * 0.9, y: B.y * 1.6, z: B.z * 0.35 },
  })
}
